/**
 * MessageActionMenu - o "⌄" que aparece no canto da bolha ao passar o mouse
 * (como no WhatsApp) e abre: Responder, Reagir, Encaminhar, Selecionar,
 * Copiar e Editar. O que o canal não tem fica DESABILITADO com o motivo
 * (regra da skill paridade-4-inboxes), nunca some.
 *
 * Compartilhado pelos 3 shells. Renderiza em portal com posição fixa (como
 * components/common/ActionMenu) para não ser cortado pelo scroll da thread.
 *
 * Props:
 *  - fromMe, colors
 *  - caps: messageActionCaps(channel)
 *  - editReason: '' quando pode editar, senão o motivo (whyCannotEdit)
 *  - hasText: a mensagem tem texto para copiar
 *  - currentReaction: reação do operador já aplicada
 *  - onReply, onReact(emoji|null), onForward, onSelect, onCopy, onEdit
 *  - alwaysVisible: mostra o gatilho sem hover (mobile)
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Reply, SmilePlus, Forward, CheckSquare, Copy, Pencil } from 'lucide-react';
import ReactionPicker from './ReactionPicker';

const MENU_W = 232;

const MessageActionMenu = ({
  fromMe = false,
  colors,
  caps,
  editReason = '',
  hasText = true,
  currentReaction = null,
  onReply,
  onReact,
  onForward,
  onSelect,
  onCopy,
  onEdit,
  alwaysVisible = false,
  forwardReason = '',
  // Cor da bolha enviada (o degradê do gatilho funde com ela). Default: o
  // verde do WhatsApp; os shells passam colors.messageSent quando têm.
  bubbleColor = null,
}) => {
  const [open, setOpen] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const menuH = reacting ? 120 : 6 * 38 + 16;
    let left = fromMe ? r.right - MENU_W : r.left;
    left = Math.max(8, Math.min(left, vw - MENU_W - 8));
    let top = r.bottom + 4;
    if (top + menuH > vh - 8) top = Math.max(8, r.top - 4 - menuH);
    setPos({ top, left });
  }, [open, reacting, fromMe]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false); setReacting(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setReacting(false); } };
    const onScroll = () => { setOpen(false); setReacting(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const close = () => { setOpen(false); setReacting(false); };
  const run = (fn) => () => { close(); if (fn) fn(); };

  const items = [
    { key: 'reply', label: 'Responder', icon: Reply, onClick: onReply,
      disabled: !caps?.quote, hint: caps?.quote ? '' : caps?.whyQuote },
    { key: 'react', label: 'Reagir', icon: SmilePlus, onClick: () => setReacting(true), keepOpen: true,
      disabled: !caps?.reactions, hint: caps?.reactions ? '' : caps?.whyReactions },
    { key: 'forward', label: 'Encaminhar', icon: Forward, onClick: onForward,
      disabled: !!forwardReason, hint: forwardReason },
    { key: 'select', label: 'Selecionar', icon: CheckSquare, onClick: onSelect },
    { key: 'copy', label: 'Copiar texto', icon: Copy, onClick: onCopy, disabled: !hasText,
      hint: hasText ? '' : 'Sem texto para copiar' },
    { key: 'edit', label: 'Editar', icon: Pencil, onClick: onEdit,
      disabled: !!editReason, hint: editReason },
  // Item sem handler só some quando está HABILITADO sem ação (o shell não
  // oferece). Desabilitado fica à vista com o motivo (regra de paridade).
  ].filter((it) => it.onClick || it.disabled || it.key === 'react');

  const trigger = (
    <button
      ref={btnRef}
      type="button"
      className="msg-action-trigger"
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); setReacting(false); }}
      title="Ações da mensagem"
      aria-label="Ações da mensagem"
      style={{
        position: 'absolute', top: 2, right: 4, zIndex: 3,
        width: 24, height: 24, borderRadius: 12, border: 'none',
        background: `linear-gradient(90deg, transparent, ${bubbleColor || (fromMe ? '#d9fdd3' : colors.bgPrimary)} 40%)`,
        color: colors.textSecondary, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: open || alwaysVisible ? 1 : undefined,
      }}
    >
      <ChevronDown size={18} />
    </button>
  );

  const menu = open ? createPortal(
    <div
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 10000 }}
    >
      {reacting ? (
        <ReactionPicker
          current={currentReaction}
          colors={colors}
          onPick={(emoji) => { close(); if (onReact) onReact(emoji); }}
        />
      ) : (
        <div style={{
          width: MENU_W, padding: 6, borderRadius: 12,
          backgroundColor: colors.bgPrimary, border: `1px solid ${colors.borderColor}`,
          boxShadow: '0 10px 30px rgba(0,0,0,0.16)',
        }}>
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.key}
                type="button"
                disabled={it.disabled}
                title={it.hint || it.label}
                onClick={it.keepOpen ? it.onClick : run(it.onClick)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '8px 10px', borderRadius: 8, border: 'none', textAlign: 'left',
                  backgroundColor: 'transparent', color: colors.textPrimary, fontSize: 14,
                  cursor: it.disabled ? 'not-allowed' : 'pointer', opacity: it.disabled ? 0.45 : 1,
                }}
                onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.backgroundColor = colors.bgTertiary; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icon size={16} color={colors.textSecondary} />
                <span style={{ flex: 1 }}>{it.label}</span>
                {it.disabled && it.hint && (
                  <span style={{ fontSize: 10, color: colors.textTertiary, maxWidth: 110, textAlign: 'right', lineHeight: 1.2 }}>
                    {it.hint}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {trigger}
      {menu}
    </>
  );
};

export default MessageActionMenu;
